import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { toast }                                   from 'react-toastify';

import { MyContext }          from '../../../../Context/MyContext';
// import categorys_json         from '../../../../json/categorys';
import AminitiesBlock         from './Aminities';
import LandAreaBlock          from './LandAreaBlock';
import API                    from '../../../../Api/api';

import {
  fieldVisible, fieldRequired,
  useDraft, RenderField,
  VideoBlock, LocationBlock, KVAddMore, PlanBlock,
  CompanyProfileBlock,
  DraftModal, ICONS, IC,
} from './formUtils';

import './ProeprtyCreate.css';
import './LandAreaBlock.css';
import './MapModal.css';
import Dropdwon from './Dropdwon';
import { formValuesFromCustomDropdowns } from '../../../../utils/dropdownCustomValue';
import { getPropertyDraftKey } from '../../../../utils/draftKeys';
import Loader from '../../../../Config/Loader';
import AppIcon from '../../../../Components/AppIcon';
import { handleUserKycApiError } from '../../../../utils/userKyc';
import PropertyOwnerNamePanel from '../../../../Admin/Components/PropertyOwnerNamePanel';

const propFieldVisible  = (f, t, c, o) => fieldVisible(f, t, c, o);
const propFieldRequired = (f, t, c, o) => fieldRequired(f, t, c, o);

const formatMetaValue = (value) => {
  if (!value) return '';
  const text = String(value).replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

// ✅ FieldsGrid — uses block_key to self-assign (no field_keys needed)
const FieldsGrid = ({ block, formData, onChange, errors, typeId, catId, isCatOther, propertyInputs }) => {
  const allInputs = Object.values(propertyInputs || {});
  const visible = allInputs
    .filter(f => f.block_key === block.block_key)
    .filter(f => f.is_show && !f.landtype && !f.is_price_field)
    .filter(f => propFieldVisible(f, typeId, catId, isCatOther))
    .sort((a, b) => (a.id || 0) - (b.id || 0));
  if (!visible.length) return null;
  return (
    <div className={`fields-grid${block.block_key === 'images' ? ' img-grid' : ''}`}>
      {visible.map(f => {
        const req = propFieldRequired(f, typeId, catId, isCatOther);
        return (
          <div key={f.id} className={f.type === 'textarea' ? 'f-full' : ''}>
            <RenderField fieldCfg={f} formData={formData} onChange={onChange} errors={errors} req={req}/>
          </div>
        );
      })}
    </div>
  );
};

const PropertyCreate = ({ isOwnProperty = false, listingTypeProp = null, draftScope = null, enableOwnerNamePanel = false }) => {
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();
  const location       = useLocation();

  const {
    createProperty, updateProperty, saveDraftToBackend, properties, fetchPropertyById,
    propertyBlocks, propertyInputs,
    propertyTypes, PROJECT_TYPE_SHOW,categorys
  } = useContext(MyContext);

  const urlRole     = searchParams.get('role')     || null;
  const urlListType = searchParams.get('listtype') || null;
  const editId      = searchParams.get('editId')   || null;
  const isEditMode  = !!editId;

  const resolvedRole = useMemo(() => {
    if (urlRole) return urlRole;
    if (location.pathname.startsWith('/admin')) return 'admin';
    if (location.pathname.startsWith('/partner')) return 'partner';
    return null;
  }, [urlRole, location.pathname]);

  const draftKey = useMemo(
    () => getPropertyDraftKey(draftScope, { editId }),
    [draftScope, editId],
  );

  const {
    formData, setFormData, onChange,
    saveDraft, draftSaved, setDraftSaved,
    showDraftModal, restoreDraft, discardDraft, clearDraft,
  } = useDraft(draftKey, { enabled: !isEditMode });

  const resolvedListType = urlListType || listingTypeProp || formData.listing_type || null;

  const [errors,      setErrors]      = useState({});
  const [toggles,     setToggles]     = useState({});
  const [submitting,  setSubmitting]  = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  // Seed form with listing type / own-property flags in one update (avoids extra draft writes)
  useEffect(() => {
    if (isEditMode) return;
    setFormData((prev) => {
      const next = { ...prev };
      let changed = false;
      const seedListType = urlListType || listingTypeProp;
      if (seedListType && !next.listing_type) {
        next.listing_type = seedListType;
        changed = true;
      }
      if (isOwnProperty && !next.is_own_property) {
        next.is_own_property = true;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, []); // eslint-disable-line

  const safeInputs = (() => {
    if (!propertyInputs) return {};
    if (Array.isArray(propertyInputs)) {
      const obj = {};
      propertyInputs.forEach(f => { if (f.field) obj[f.field] = f; });
      return obj;
    }
    return propertyInputs;
  })();

  const safeBlocks = Array.isArray(propertyBlocks) ? propertyBlocks : [];

  useEffect(() => {
    if (!isEditMode) return;
    const mapToForm = (raw) => ({
      ...raw,
      ...(raw.extra_fields || {}),
      ...formValuesFromCustomDropdowns(raw),
      _prev_property_owner_number: raw.property_owner_number || '',
      type_id:   raw.type_id   ?? 1,
      // ✅ restore preset IDs + custom strings together
      aminities: [...(raw.amenities_id || []), ...(raw.custom_amenities || [])],
      // ✅ Restore videos with thumbnails from saved videos array
      videos: (() => {
        if (raw.videos?.length) {
          return raw.videos.map((v, i) => ({
            id: Date.now() + i,
            thumbnail: v.thumbnail || null,   // string path → FileUpload shows preview
            video_link: v.video_link || '',
          }));
        }
        return (raw.youtube_links || []).map((link, i) => ({
          id: Date.now() + i, thumbnail: null, video_link: link,
        }));
      })(),
      shorts: (raw.shorts_links  || []).map((link, i) => ({ id: Date.now() + 1000 + i, short_link: link })),
      property_plan: (raw.property_plan || []).map((p, i) => ({ ...p, id: p.id || Date.now() + i, image: p.image || null })),
      enable_video_section: !!(raw.youtube_links?.length || raw.shorts_links?.length),
      enable_plan_section:  !!(raw.property_plan?.length),
      address: raw.address || raw.location || '',
    });

    const loadData = async () => {
      // 1. Try local storage drafts
      try {
        const stored = JSON.parse(localStorage.getItem(draftKey));
        const arr    = Array.isArray(stored) ? stored : stored ? [stored] : [];
        const found  = arr.find(d => String(d.id) === String(editId));
        if (found) { setFormData(mapToForm(found)); return; }
      } catch {}

      // 2. Try context properties
      const existing = properties.find(p => String(p.id) === String(editId));
      if (existing) {
        setFormData(mapToForm(existing));
        return;
      }

      // 3. Fetch from backend — admin/partner use authenticated listings API
      try {
        const isStaff = location.pathname.startsWith('/admin') || location.pathname.startsWith('/partner');
        let fetched = null;
        if (isStaff) {
          try {
            const res = await API.get(`/admin/listings/${editId}`);
            fetched = res.data;
          } catch {
            fetched = null;
          }
        }
        if (!fetched) {
          fetched = await fetchPropertyById(editId);
        }
        if (fetched) setFormData(mapToForm(fetched));
        else toast.error('Failed to load property data.');
      } catch (err) {
        console.error('Failed to fetch property for edit:', err);
        toast.error('Failed to load property data.');
      }
    };

    loadData();
  }, [editId, properties.length]); // eslint-disable-line

  useEffect(() => {
    if (!isEditMode) return;
    setToggles({
      enable_video_section: formData.enable_video_section ?? false,
      enable_plan_section:  formData.enable_plan_section  ?? false,
    });
  }, [formData.enable_video_section, formData.enable_plan_section, isEditMode]); // eslint-disable-line

  const typeId = formData.type_id || null;
  const catId  = formData.category_id || null;
  const toNum = (v) => Number(v);
  // const categorys    = Array.isArray(categorys) ? categorys : [];
  const catObj       = catId
    ? categorys.find((c) => {
        const cid = Number(c?.id ?? c?.category_id ?? c?.categoryId);
        return !Number.isNaN(cid) && cid === Number(catId);
      })
    : null;
  const isCatOther   = catObj?.is_other === true;
  const infoReady    = !!(typeId && catId);
  const filteredCategorys = typeId
    ? categorys.filter((c) => {
        const typeIds = Array.isArray(c?.type_id) ? c.type_id.map(toNum) : [];
        const isActive = c?.is_active !== false;
        return isActive && typeIds.includes(toNum(typeId));
      })
    : [];

  const handleTypeChange     = (f, v) => { setFormData(p => ({ ...p, type_id: v, category_id: '' })); setErrors({}); setDraftSaved(false); };
  const handleCategoryChange = (f, v) => { setFormData(p => ({ ...p, category_id: v })); setErrors({}); setDraftSaved(false); };

  const isBlockEnabled = block => {
    if (!block.enable_toggle) return true;
    return block.toggle_field in toggles ? toggles[block.toggle_field] : (block.default_enabled !== false);
  };

  const validate = () => {
    const errs = {};
    if (!typeId) errs.type_id = 'Type is required';
    if (!catId)  errs.category_id = 'Category is required';
    Object.values(safeInputs).forEach(f => {
      if (!f.is_show || !propFieldVisible(f, typeId, catId, isCatOther) || !propFieldRequired(f, typeId, catId, isCatOther)) return;
      if (!formData[f.field] && formData[f.field] !== 0) errs[f.field] = `${f.label} is required`;
    });
    if (!formData.state_id) errs.state_id = 'State is required';
    if (!formData.city_id)  errs.city_id  = 'City is required';
    if (!formData.address)  errs.address  = 'Address is required';
    setErrors(errs);
    return !Object.keys(errs).length;
  };

 const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validate()) { 
    document.querySelector('.error-msg')?.scrollIntoView({ behavior: 'smooth', block: 'center' }); 
    return; 
  }
  
  setSubmitting(true);
  try {
    if (isEditMode) { 
      await updateProperty(editId, formData, resolvedRole, resolvedListType); 
      toast.success('Property updated!'); 
    } else { 
      await createProperty(formData, resolvedRole, resolvedListType); 
      clearDraft(); 
      toast.success('Property submitted!'); 
    }
    // REMOVED: All the navigate() calls are gone. 
    // The loader will stay visible until the user clicks OK.
  } catch (err) { 
    setSubmitting(false);
    if (!handleUserKycApiError(err, {
      toast,
      navigate,
      pendingFlow: {
        context: 'property',
        role: resolvedRole,
        listtype: resolvedListType,
        blockedAt: 'submit',
      },
    })) {
      toast.error(err.response?.data?.error || 'Something went wrong.'); 
    }
  }
};

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      const saved = await saveDraftToBackend(formData, resolvedRole, resolvedListType);
      if (saved?.id && !formData.id) setFormData(p => ({ ...p, id: saved.id }));
      saveDraft(); toast.success('Draft saved!'); setDraftSaved(true);
    } catch { saveDraft(); toast.info('Draft saved locally.'); }
    finally { setSavingDraft(false); }
  };

  const renderBlock = block => {
    const enabled = isBlockEnabled(block);
    const shared  = { formData, onChange, errors };
    switch (block.block_type) {
      case 'fields':    return <FieldsGrid block={block} {...shared} typeId={typeId} catId={catId} isCatOther={isCatOther} propertyInputs={safeInputs}/>;
      case 'land_area': return infoReady ? <LandAreaBlock inputs={safeInputs} formData={formData} onChange={onChange} errors={errors} typeId={typeId} catId={catId} isOther={isCatOther} visibleFn={propFieldVisible} requiredFn={propFieldRequired}/> : null;
      case 'video':     return enabled ? <VideoBlock formData={formData} onChange={onChange}/> : <p className="disabled-hint">Enable this section to add videos.</p>;
      case 'location':  return <LocationBlock {...shared}/>;
      case 'amenities': return <AminitiesBlock value={formData.aminities || []} onChange={onChange}/>;
      case 'key_value': return <KVAddMore block={block} {...shared}/>;
      case 'plan':      return enabled ? <PlanBlock formData={formData} onChange={onChange}/> : <p className="disabled-hint">Enable this section to add plans.</p>;
      case 'company_profile': return <CompanyProfileBlock formData={formData} onChange={onChange} errors={errors}/>;
      default:          return null;
    }
  };

  const infoBlock = safeBlocks.find(b => b.block_key === 'information');

  return (
    <div className="create-page">
      {enableOwnerNamePanel && (
        <PropertyOwnerNamePanel
          name={formData.property_owner_name || ''}
          number={formData.property_owner_number || ''}
          onChange={({ name, number }) => {
            onChange('property_owner_name', name);
            onChange('property_owner_number', number);
          }}
        />
      )}
      {submitting && <Loader 
  label={isEditMode ? 'Updating' : 'Submitting'} 
  onDone={() => {
    const path = window.location.pathname;
    if (path.startsWith('/admin')) navigate('/admin/properties', { replace: true });
    else if (path.startsWith('/partner')) navigate('/partner/properties', { replace: true });
    else navigate('/account', { replace: true });
  }} 
/>}
      {showDraftModal && !isEditMode && <DraftModal onRestore={restoreDraft} onDiscard={discardDraft}/>}
      <div className="page-header">
        <div className="breadcrumb">
          <span style={{ cursor:'pointer', textDecoration:'underline' }} onClick={() => {
            const path = window.location.pathname;
            if (path.startsWith('/admin')) navigate('/admin/properties');
            else if (path.startsWith('/partner')) navigate('/partner/properties');
            else isEditMode ? navigate('/account') : navigate(-1);
          }}>Dashboard</span>
          {' › '}{isEditMode ? 'Edit Property' : 'Create Property'}
        </div>
        <h1 className="page-title">{isEditMode ? 'Edit Property' : 'Create Property'}</h1>
        {(resolvedRole || resolvedListType) && (
          <div className="page-header-badges" aria-label="Listing context">
            {resolvedRole && (
              <span className="page-header-chip">
                <span className="page-header-chip__label">Role</span>
                <span className="page-header-chip__value">{formatMetaValue(resolvedRole)}</span>
              </span>
            )}
            {resolvedListType && (
              <span className="page-header-chip page-header-chip--type">
                <span className="page-header-chip__label">Type</span>
                <span className="page-header-chip__value">{formatMetaValue(resolvedListType)}</span>
              </span>
            )}
          </div>
        )}
      </div>

      <form className="create-form" onSubmit={handleSubmit} noValidate>
        <div className="form-block">
          <div className="block-header">
            <div className="block-title-icon">{ICONS.info}</div>
            <h2 className="block-title">Property Information</h2>
          </div>
          <div className="block-body">
            <div className="fields-grid tc-row">
              <Dropdwon label="Property Type" field="type_id" options={propertyTypes}
                value={typeId || ''} onChange={handleTypeChange} placeholder="Select type"
                isrequired labelKey="type_name" valueKey="id" error={errors.type_id}/>
              {typeId ? (
                <Dropdwon label="Category" field="category_id" options={filteredCategorys}
                  value={catId || ''} onChange={handleCategoryChange} placeholder="Select category"
                  isrequired labelKey="category_name" valueKey="id" error={errors.category_id}/>
              ) : (
                <div className="tc-cat-pending"><span className="tc-cat-pending-text">← Select a type first</span></div>
              )}
            </div>
            {infoReady && infoBlock && (
              <div style={{ marginTop: 20 }}>
                <FieldsGrid block={infoBlock} formData={formData} onChange={onChange} errors={errors}
                  typeId={typeId} catId={catId} isCatOther={isCatOther} propertyInputs={safeInputs}/>
              </div>
            )}
            {!infoReady && <p className="type-hint" style={{ marginTop: 12 }}>{!typeId ? <><AppIcon name="pointer" size={14} /> Select a property type to get started.</> : <><AppIcon name="pointer" size={14} /> Select a category to continue.</>}</p>}
          </div>
        </div>

        {safeBlocks.filter(b => b.is_property_show === true && b.block_key !== 'information').map(block => {
          if (block.block_type === 'land_area' && !infoReady) return null;
          const content = renderBlock(block);
          if (content === null) return null;
          return (
            <div key={block.id} className="form-block">
              <div className="block-header">
                <div className={`block-title-icon ${IC[block.block_icon] || ''}`}>{ICONS[block.block_icon] || ICONS.info}</div>
                <h2 className="block-title">{block.block_name}</h2>
                {block.enable_toggle && (
                  <label className="form-block-toggle" title={isBlockEnabled(block) ? 'Section enabled' : 'Section disabled'}>
                    <input
                      type="checkbox"
                      checked={isBlockEnabled(block)}
                      onChange={() => {
                        const n = !isBlockEnabled(block);
                        setToggles((p) => ({ ...p, [block.toggle_field]: n }));
                        onChange(block.toggle_field, n);
                      }}
                    />
                    <span className="form-block-toggle-track" aria-hidden />
                    <span className="form-block-toggle-text">
                      {isBlockEnabled(block) ? 'Enabled' : 'Disabled'}
                    </span>
                  </label>
                )}
              </div>
              <div className="block-body">{content}</div>
            </div>
          );
        })}

        <div className="submit-row">
          {!isEditMode && (
            <button type="button" className={`draft-btn${draftSaved ? ' saved' : ''}`} onClick={handleSaveDraft} disabled={savingDraft}>
              <span className="draft-icon">{savingDraft ? <AppIcon name="loader" size={16} spin /> : draftSaved ? <AppIcon name="check" size={16} /> : <AppIcon name="save" size={16} />}</span>
              {savingDraft ? 'Saving…' : draftSaved ? 'Saved!' : 'Save Draft'}
            </button>
          )}
          <button type="submit" className="submit-btn" disabled={submitting}>
            {submitting ? (isEditMode ? 'Updating…' : 'Submitting…') : (isEditMode ? 'Update Property' : 'Submit Property')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PropertyCreate;